import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { clientPortalApi } from "./clientPortalApi";
import { TimelineRail } from "./TimelineRail";
import { UpdatesFeed } from "./UpdatesFeed";
import { DocumentsList } from "./DocumentsList";
import { BtsGallery } from "./BtsGallery";
import { DeliverablePlayer } from "./DeliverablePlayer";
import { MessagesThread } from "./MessagesThread";
import { MeetingsList } from "./MeetingsList";
import { ChevronLeft, ExternalLink, Film, Mail, MessageCircle, MessageSquare, PlayCircle, Video } from "lucide-react";
import { getBtsSeenAt, markBtsSeen, countNewBts } from "./btsNudge";
import { useDocumentTitle } from "./useDocumentTitle";
import { formatShootDates } from "@/utils/formatShootDates";
import "./clientPortal.css";

// Client Portal v2 (2026-09, Phase 1, §4/§5) — 10 hash-sections consolidated to 4, per the
// approved prompt: Overview (facts + timeline + PM + latest update), Review & Delivery
// (deliverables grouped by state + final films + BTS), Communication (Messages first, then
// Meetings, then Updates), Documents (unchanged, restyled). Ask NJM/Assistant is removed from
// this UI entirely — its backend route (modules/client_ai.py) is left untouched, just unused.
const SECTIONS = [
  { id: "overview", label: "Overview", shortLabel: "Overview" },
  { id: "review", label: "Review & Delivery", shortLabel: "Review" },
  { id: "communication", label: "Communication", shortLabel: "Comms" },
  { id: "documents", label: "Documents", shortLabel: "Docs" },
];

// Deliverable-state grouping (§4, confirmed against the real review_links enum — NOT the
// enum the prompt assumed): "Superseded" is hidden outright (an older version a newer one has
// replaced — nothing left for a client to act on). Everything else visible but not yet in an
// active-review or finished state (Draft/Internal Review/Ready for Client) reads as "in
// progress" to a client, which is exactly what it is.
const CURRENT_REVIEW_STATUSES = new Set(["Shared with Client", "Feedback Received"]);
const COMPLETED_STATUSES = new Set(["Approved", "Final"]);
function groupDeliverables(items) {
  const current = [], inProgress = [], completed = [];
  for (const d of items) {
    if (d.status === "Superseded") continue;
    if (CURRENT_REVIEW_STATUSES.has(d.status)) current.push(d);
    else if (COMPLETED_STATUSES.has(d.status)) completed.push(d);
    else inProgress.push(d);
  }
  return { current, inProgress, completed };
}

const MESSAGES_POLL_MS = 15000;

// Perf fix (2026-10, Cursor audit item 6 / group 0B) — every section below (deliverables,
// films, timeline, updates, documents, bts, messages, meetings) used to be one all-or-nothing
// Promise.all: a single slow or failing call blanked the ENTIRE page behind a loading
// skeleton, or a single non-404 failure left every section unmounted. Each section now loads
// independently via this hook — its own loading state, its own error+retry, and it renders
// the moment ITS OWN call resolves, never waiting on (or being blocked by) any other section.
// No change to any client serializer or project-ownership check — every call below is the
// EXACT SAME clientPortalApi.* request the old Promise.all made, just no longer bundled.
function useAsyncSection(loadFn, deps) {
  const [state, setState] = useState({ data: null, loading: true, error: false });
  // Code-review fix (2026-10, Cursor audit item 9) — a stale, in-flight response for a
  // PREVIOUS `id` (deps change) could otherwise land after the new id's own fresher response
  // and silently overwrite it. `genRef` is a per-hook-instance generation counter; only the
  // most recently started `load()` call is allowed to commit its result.
  const genRef = useRef(0);
  const load = useCallback(() => {
    const myGen = ++genRef.current;
    setState((s) => ({ ...s, loading: true, error: false }));
    return loadFn().then((data) => { if (myGen === genRef.current) setState({ data, loading: false, error: false }); })
      .catch(() => { if (myGen === genRef.current) setState((s) => ({ ...s, loading: false, error: true })); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { load(); }, [load]);
  const setData = useCallback((updater) => setState((s) => ({ ...s, data: typeof updater === "function" ? updater(s.data) : updater })), []);
  return [state, load, setData];
}

function SectionSkeleton({ testId }) {
  return <div className="h-14 bg-black/5 animate-pulse" data-testid={testId} />;
}

function SectionError({ onRetry, label, testId }) {
  return (
    <div className="flex items-center gap-3 text-[12.5px] text-[color:var(--cp-grey-3)]" data-testid={testId}>
      <span>Couldn't load {label}.</span>
      <button onClick={onRetry} className="font-semibold text-[color:var(--cp-gold-dim)] underline hover:text-black transition-colors" data-testid={`${testId}-retry`}>Retry</button>
    </div>
  );
}

// Frame.io Review Integration V1 (2026-09, audit-approved): Frame.io remains the canonical
// review surface (timecoded comments/approvals live there) — this row never opens NJM's own
// feedback/approve modal for a Frame.io deliverable, it opens the real Frame.io Share URL
// directly in a new tab. PM status transitions (Draft -> ... -> Approved/Final) are set
// independently via the staff ReviewLinks.jsx editor, unaffected by this client-side change.
function FrameIoDeliverableRow({ d }) {
  const meta = [d.version_name, d.date_added ? `Uploaded ${d.date_added}` : null, d.expiry ? `Feedback requested by ${d.expiry}` : null].filter(Boolean).join(" · ");
  return (
    <div data-testid={`client-portal-deliverable-${d.id}`} className="cp-row-divider flex items-center justify-between gap-4 py-3.5 w-full -mx-2 px-2">
      <div className="flex items-center gap-3.5 min-w-0">
        <Film className="h-4 w-4 text-[color:var(--cp-gold-dim)] shrink-0" />
        <div className="min-w-0">
          <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--cp-gold-dim)]">Frame.io Review</div>
          <div className="text-[13.5px] font-medium text-[color:var(--cp-ink-text)] truncate">{d.title || d.version_name || "Untitled cut"}</div>
          {meta && <div className="text-[11px] text-[color:var(--cp-grey-3)]">{meta}</div>}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <a href={d.url} target="_blank" rel="noopener noreferrer" data-testid={`client-portal-deliverable-frameio-open-${d.id}`}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--cp-gold-dim)] hover:text-black transition-colors">
          Review &amp; Comment <ExternalLink className="h-3.5 w-3.5" />
        </a>
        <span className="text-[10px] text-[color:var(--cp-grey-4)] text-right max-w-[180px]">Opens securely in Frame.io for timecoded comments and approvals.</span>
      </div>
    </div>
  );
}

function DeliverableRow({ d, onOpen }) {
  if (d.platform === "Frame.io") return <FrameIoDeliverableRow d={d} />;
  return (
    <button onClick={() => onOpen(d)} data-testid={`client-portal-deliverable-${d.id}`}
      className="cp-row-divider flex items-center justify-between gap-4 py-3.5 hover:bg-black/[.02] transition-colors w-full text-left -mx-2 px-2">
      <div className="flex items-center gap-3.5 min-w-0">
        <Video className="h-4 w-4 text-[color:var(--cp-grey-3)] shrink-0" />
        <div className="min-w-0">
          <div className="text-[13.5px] font-medium text-[color:var(--cp-ink-text)] truncate">{d.title || d.version_name || "Untitled cut"}</div>
          <div className="text-[11px] text-[color:var(--cp-grey-3)]">{d.platform}{d.version_name ? ` · ${d.version_name}` : ""}</div>
        </div>
      </div>
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--cp-grey-4)] shrink-0"><MessageSquare className="h-3.5 w-3.5" /> Review</span>
    </button>
  );
}

/** Client Portal Project page (v2, 2026-09) — ONE calm, scrollable canvas on the warm content
 * surface with a sticky desktop sub-nav + mobile segmented control (never scrolling tabs). */
export default function ClientPortalProject() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  useDocumentTitle(project?.name || "Project");
  const [pm, setPm] = useState(null);
  const [projectLoading, setProjectLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeDeliverable, setActiveDeliverable] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");
  const [lastViewedMessagesAt, setLastViewedMessagesAt] = useState(() => localStorage.getItem(`cp_messages_seen_${id}`) || null);
  const [lastViewedBtsAt, setLastViewedBtsAt] = useState(() => getBtsSeenAt(id));
  const pollRef = useRef(null);
  const [sendingMessage, setSendingMessage] = useState(false);

  // The ONLY call that gates the page shell (header, back link, status badge, notFound) —
  // every other read below is its own independent section, per item 6. Same
  // clientPortalApi.getProject(id) request/response shape as before — no serializer change.
  useEffect(() => {
    setProjectLoading(true);
    setNotFound(false);
    clientPortalApi.getProject(id)
      .then((p) => { setProject(p.data.project); setPm(p.data.pm); })
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); })
      .finally(() => setProjectLoading(false));
  }, [id]);

  const [deliverablesState, reloadDeliverables] = useAsyncSection(() => clientPortalApi.getDeliverables(id).then((r) => r.data.items || []), [id]);
  const [filmsState, reloadFilms] = useAsyncSection(() => clientPortalApi.getFilms(id).then((r) => r.data.items || []), [id]);
  const [timelineState, reloadTimeline] = useAsyncSection(() => clientPortalApi.getTimeline(id).then((r) => r.data.stages || []), [id]);
  const [updatesState, reloadUpdates, setUpdatesData] = useAsyncSection(() => clientPortalApi.getUpdates(id).then((r) => r.data.items || []), [id]);
  const [documentsState, reloadDocuments] = useAsyncSection(() => clientPortalApi.getDocuments(id).then((r) => r.data.items || []), [id]);
  const [btsState, reloadBts] = useAsyncSection(() => clientPortalApi.getBts(id).then((r) => r.data.items || []), [id]);
  const [messagesState, reloadMessages, setMessagesData] = useAsyncSection(() => clientPortalApi.getMessages(id).then((r) => r.data.items || []), [id]);
  const [meetingsState, reloadMeetings] = useAsyncSection(
    () => clientPortalApi.getMeetings(id).then((r) => ({ items: r.data.items || [], calendlyUrl: r.data.calendly_url || "" })), [id]
  );

  const deliverables = useMemo(() => deliverablesState.data || [], [deliverablesState.data]);
  const films = filmsState.data || [];
  const timeline = timelineState.data || [];
  const updates = updatesState.data || [];
  const documents = documentsState.data || [];
  const bts = btsState.data || [];
  const messages = messagesState.data || [];
  const meetings = meetingsState.data?.items || [];
  const calendlyUrl = meetingsState.data?.calendlyUrl || "";

  // Messages poll while the tab itself is visible (Code-review fix, 2026-10, Cursor audit
  // item 2) — previously also gated on `activeSection === "communication"`, so the unread
  // badge in the sub-nav went stale the instant a client scrolled away from Communication.
  // The fetch itself is cheap (same `getMessages` call the section already makes on open), so
  // there is no separate lightweight "count only" endpoint to keep in sync with — it just keeps
  // running regardless of which section is in view, same as before only while hidden.
  useEffect(() => {
    if (!project) return;
    // Code-review fix (2026-10, Cursor audit P-small/B9): same stale-response guard as
    // useAsyncSection's own `genRef` (ClientPortalProject.jsx's `load()`), just expressed as a
    // closure flag — this poll calls `setMessagesData` directly (not through `load()`), so it
    // needed its OWN guard: without it, an in-flight GET for project A's messages could still
    // resolve and land on project B's state after a fast project-to-project navigation.
    let cancelled = false;
    const tick = () => {
      if (document.hidden) return;
      clientPortalApi.getMessages(id).then((r) => {
        if (cancelled) return;
        setMessagesData((prev) => {
          const pending = (prev || []).filter((m) => m.pending);
          return [...(r.data.items || []), ...pending];
        });
      }).catch(() => {});
    };
    pollRef.current = setInterval(tick, MESSAGES_POLL_MS);
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { cancelled = true; clearInterval(pollRef.current); document.removeEventListener("visibilitychange", onVisible); };
  }, [id, project, setMessagesData]);

  // Mobile segmented control + desktop sub-nav both need to know which of the 4 sections is
  // currently in view, so a single IntersectionObserver drives both (no scroll-position math
  // duplicated per breakpoint).
  useEffect(() => {
    if (!project) return;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) setActiveSection(visible.target.id);
    }, { rootMargin: "-30% 0px -50% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] });
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [project]);

  const onSendMessage = (text) => {
    markMessagesSeen();
    const tempId = `pending-${Date.now()}`;
    setMessagesData((prev) => [...(prev || []), { id: tempId, sender_role: "client", sender_name: "You", body: text, pending: true }]);
    setSendingMessage(true);
    clientPortalApi.sendMessage(id, text)
      .then((r) => { setMessagesData((prev) => (prev || []).map((m) => (m.id === tempId ? r.data.message : m))); })
      .catch(() => { setMessagesData((prev) => (prev || []).filter((m) => m.id !== tempId)); })
      .finally(() => setSendingMessage(false));
  };

  const onReact = (updateId) => {
    setUpdatesData((prev) => (prev || []).map((u) => (u.id === updateId ? { ...u, reacted_by_me: !u.reacted_by_me, reaction_count: u.reaction_count + (u.reacted_by_me ? -1 : 1) } : u)));
    clientPortalApi.reactToUpdate(updateId).catch(() => {
      setUpdatesData((prev) => (prev || []).map((u) => (u.id === updateId ? { ...u, reacted_by_me: !u.reacted_by_me, reaction_count: u.reaction_count + (u.reacted_by_me ? -1 : 1) } : u)));
    });
  };

  const unreadMessagesCount = messages.filter((m) => m.sender_role === "staff" && !m.pending && (!lastViewedMessagesAt || (m.created_at || "") > lastViewedMessagesAt)).length;

  const markMessagesSeen = () => {
    const now = new Date().toISOString();
    localStorage.setItem(`cp_messages_seen_${id}`, now);
    setLastViewedMessagesAt(now);
  };

  const newBtsCount = countNewBts(bts.map((m) => m.created_at), lastViewedBtsAt);
  const markBtsSeenNow = () => setLastViewedBtsAt(markBtsSeen(id));

  const { current, inProgress, completed } = useMemo(() => groupDeliverables(deliverables), [deliverables]);
  const latestUpdate = updates[0];

  const goTo = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (sectionId === "communication") markMessagesSeen();
    if (sectionId === "review") markBtsSeenNow();
  };

  // Quick-Start Onboarding (2026-09) + Help menu's "Message your NJM contact" both deep-link
  // here via ?scrollTo=<sectionId> (never a route change) — scroll once loaded, then clear it.
  const scrollToParam = searchParams.get("scrollTo");
  useEffect(() => {
    if (projectLoading || !project || !scrollToParam) return;
    goTo(scrollToParam);
    setSearchParams({}, { replace: true });
  }, [projectLoading, project, scrollToParam]); // eslint-disable-line react-hooks/exhaustive-deps

  if (projectLoading) {
    return <div className="cp-theme min-h-screen px-6 md:px-16 py-14" data-testid="client-portal-project-loading"><div className="h-8 w-72 bg-black/5 animate-pulse" /></div>;
  }

  if (notFound || !project) {
    return (
      <div className="cp-theme min-h-screen px-6 md:px-16 py-14" data-testid="client-portal-project-not-found">
        <p className="text-[15px] text-[color:var(--cp-grey-3)] mb-4">We couldn't find that project.</p>
        <Link to="/client-portal/home" className="text-sm text-[color:var(--cp-gold-dim)] inline-flex items-center gap-1"><ChevronLeft className="h-4 w-4" /> Back to Home</Link>
      </div>
    );
  }

  const badgeCount = { communication: unreadMessagesCount, review: newBtsCount };

  return (
    <div className="cp-theme min-h-screen cp-enter" data-testid="client-portal-project-page">
      <div className="px-4 md:px-16 pt-9 pb-6 max-w-[1200px] mx-auto">
        <Link to="/client-portal/home" className="text-xs text-[color:var(--cp-grey-3)] hover:text-black inline-flex items-center gap-1 mb-4"><ChevronLeft className="h-3.5 w-3.5" /> Home</Link>
        <div className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide px-2.5 py-1 mb-3 bg-black/5 text-[color:var(--cp-grey-1)]">{project.status_label}</div>
        <h1 className="cp-display text-[26px] sm:text-[30px] md:text-[42px] text-[color:var(--cp-ink-text)]">{project.name}</h1>
      </div>

      {/* Desktop sub-nav */}
      <nav className="hidden md:block sticky z-20 bg-[color:var(--cp-surface)] border-b border-[color:var(--cp-surface-line)] px-6 md:px-16" style={{ top: "var(--cp-total-header-h, 62px)" }} data-testid="client-portal-project-subnav">
        <div className="max-w-[1200px] mx-auto flex gap-7">
          {SECTIONS.map((s) => (
            <button key={s.id} data-testid={`client-portal-project-anchor-${s.id}`} onClick={() => goTo(s.id)}
              className={`relative text-[11.5px] font-semibold uppercase tracking-wider py-3.5 transition-colors ${activeSection === s.id ? "text-black after:absolute after:left-0 after:right-0 after:bottom-0 after:h-[2px] after:bg-[color:var(--cp-gold)]" : "text-[color:var(--cp-grey-3)] hover:text-black"}`}>
              {s.label}
              {badgeCount[s.id] > 0 && (
                <span data-testid={`client-portal-badge-${s.id}`} className="ml-1.5 inline-flex min-w-[16px] h-4 px-1 rounded-full bg-[color:var(--cp-gold)] text-black text-[9px] font-bold items-center justify-center align-middle">
                  {badgeCount[s.id] > 9 ? "9+" : badgeCount[s.id]}
                </span>
              )}
            </button>
          ))}
        </div>
      </nav>
      {/* Mobile segmented control — all 4 sections always visible, never scrolling tabs */}
      <div className="md:hidden sticky z-20 grid grid-cols-4 bg-black cp-segmented" style={{ top: "var(--cp-total-header-h, 58px)" }} data-testid="client-portal-project-segmented-nav">
        {SECTIONS.map((s) => (
          <button key={s.id} onClick={() => goTo(s.id)} className={`whitespace-nowrap ${activeSection === s.id ? "active" : ""}`} data-testid={`client-portal-project-segment-${s.id}`}>
            {s.shortLabel}{badgeCount[s.id] > 0 ? ` (${badgeCount[s.id]})` : ""}
          </button>
        ))}
      </div>

      <div className="max-w-[1200px] mx-auto">
        <section id="overview" className="px-4 md:px-16 py-12 scroll-mt-28" data-testid="client-portal-section-overview">
          <span className="cp-eyebrow block mb-6">Overview</span>
          <div className="grid md:grid-cols-3 gap-8 min-w-0">
            <div className="md:col-span-2 space-y-7 min-w-0">
              {project.objective && <p className="text-[16px] leading-relaxed text-[color:var(--cp-ink-text)]">{project.objective}</p>}
              <div className="grid grid-cols-2 gap-6 max-w-[420px]">
                {project.category && (
                  <div><div className="cp-eyebrow text-[10px] mb-1">Category</div><div className="text-sm text-[color:var(--cp-grey-1)]">{project.category}</div></div>
                )}
                {formatShootDates(project.shoot_dates) && (
                  <div><div className="cp-eyebrow text-[10px] mb-1">Shoot dates</div><div className="text-sm text-[color:var(--cp-grey-1)]">{formatShootDates(project.shoot_dates)}</div></div>
                )}
                {project.shoot_locations && (
                  <div><div className="cp-eyebrow text-[10px] mb-1">Location</div><div className="text-sm text-[color:var(--cp-grey-1)]">{project.shoot_locations}</div></div>
                )}
              </div>
              {timelineState.loading && <SectionSkeleton testId="client-portal-timeline-loading" />}
              {timelineState.error && <SectionError onRetry={reloadTimeline} label="the project timeline" testId="client-portal-timeline-error" />}
              {!timelineState.loading && !timelineState.error && timeline.length > 0 && <TimelineRail stages={timeline} />}
              {updatesState.loading && <SectionSkeleton testId="client-portal-latest-update-loading" />}
              {updatesState.error && <SectionError onRetry={reloadUpdates} label="the latest update" testId="client-portal-latest-update-error" />}
              {!updatesState.loading && !updatesState.error && latestUpdate && (
                <div>
                  <div className="cp-eyebrow text-[10px] mb-2">Latest update</div>
                  <p className="text-[13.5px] text-[color:var(--cp-grey-1)] leading-relaxed">{latestUpdate.body}</p>
                  <span className="text-[10.5px] text-[color:var(--cp-grey-4)]">{latestUpdate.date}</span>
                </div>
              )}
            </div>
            {pm && (
              <div className="cp-panel p-6 self-start min-w-0" data-testid="client-portal-project-pm-card">
                <div className="cp-eyebrow text-[10px] mb-3">Your NJM contact</div>
                <div className="text-[15px] font-semibold text-[color:var(--cp-ink-text)]">{pm.name}</div>
                <div className="text-xs text-[color:var(--cp-grey-3)] mb-3">Your {pm.role_title || "Project Manager"}</div>
                <div className="flex flex-col gap-2">
                  {pm.email && <a href={`mailto:${pm.email}`} className="inline-flex items-center gap-1.5 text-xs text-[color:var(--cp-grey-1)] hover:text-black"><Mail className="h-3.5 w-3.5" /> {pm.email}</a>}
                  {pm.whatsapp && <a href={`https://wa.me/${pm.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[color:var(--cp-grey-1)] hover:text-black"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>}
                </div>
              </div>
            )}
          </div>
        </section>

        <section id="review" className="px-4 md:px-16 py-12 cp-section-rule scroll-mt-28" data-testid="client-portal-section-review">
          <span className="cp-eyebrow block mb-6">Review &amp; Delivery</span>
          {deliverablesState.loading && <SectionSkeleton testId="client-portal-deliverables-loading" />}
          {deliverablesState.error && <SectionError onRetry={reloadDeliverables} label="deliverables" testId="client-portal-deliverables-error" />}
          {!deliverablesState.loading && !deliverablesState.error && (
            <>
              {current.length > 0 && (
                <div className="mb-8" data-testid="client-portal-current-review-group">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--cp-gold-dim)] mb-1">Current review</div>
                  {current.map((d) => <DeliverableRow key={d.id} d={d} onOpen={setActiveDeliverable} />)}
                </div>
              )}
              {inProgress.length > 0 && (
                <div className="mb-8">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--cp-grey-4)] mb-1">In progress</div>
                  {inProgress.map((d) => <DeliverableRow key={d.id} d={d} onOpen={setActiveDeliverable} />)}
                </div>
              )}
              {completed.length > 0 && (
                <div className="mb-8">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--cp-grey-4)] mb-1">Completed</div>
                  {completed.map((d) => <DeliverableRow key={d.id} d={d} onOpen={setActiveDeliverable} />)}
                </div>
              )}
              {deliverables.length === 0 && (
                <p className="text-[13.5px] text-[color:var(--cp-grey-3)] mb-8" data-testid="client-portal-deliverables-empty">Your first deliverables will appear here.</p>
              )}
            </>
          )}

          {filmsState.error && <SectionError onRetry={reloadFilms} label="featured films" testId="client-portal-films-error" />}
          {!filmsState.loading && !filmsState.error && films.length > 0 && (
            <div className="mb-8">
              <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--cp-grey-4)] mb-2">Featured films</div>
              <div className="grid sm:grid-cols-2 gap-4">
                {films.map((f) => (
                  <a key={f.id} href={f.final_url} target="_blank" rel="noopener noreferrer" data-testid={`client-portal-film-${f.id}`}
                    className="cp-panel overflow-hidden group hover:border-black/30 transition-colors block">
                    <div className="relative aspect-video bg-black">
                      {f.thumbnail_url ? <img src={f.thumbnail_url} alt={f.name} className="w-full h-full object-cover" loading="lazy" decoding="async" /> : (
                        <div className="w-full h-full flex items-center justify-center"><Video className="h-6 w-6 text-[color:var(--cp-gold)]" /></div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                        <PlayCircle className="h-8 w-8 text-white opacity-0 group-hover:opacity-90 transition-opacity" />
                      </div>
                    </div>
                    <div className="p-3.5 text-[13px] font-medium text-[color:var(--cp-ink-text)] truncate">{f.name}</div>
                  </a>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--cp-grey-4)] mb-2">From the shoot</div>
            {btsState.loading && <SectionSkeleton testId="client-portal-bts-loading" />}
            {btsState.error && <SectionError onRetry={reloadBts} label="behind-the-scenes media" testId="client-portal-bts-error" />}
            {!btsState.loading && !btsState.error && <BtsGallery media={bts} fileUrl={(mediaId) => clientPortalApi.btsFileUrl(id, mediaId)} />}
          </div>
        </section>

        <section id="communication" className="px-4 md:px-16 py-12 cp-section-rule scroll-mt-28" data-testid="client-portal-section-communication">
          <span className="cp-eyebrow block mb-6">Communication</span>
          <div className="mb-9">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--cp-grey-4)] mb-2">Messages</div>
            {messagesState.loading && <SectionSkeleton testId="client-portal-messages-loading" />}
            {messagesState.error && <SectionError onRetry={reloadMessages} label="messages" testId="client-portal-messages-error" />}
            {!messagesState.loading && !messagesState.error && <MessagesThread messages={messages} onSend={onSendMessage} sending={sendingMessage} />}
          </div>
          <div className="mb-9">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--cp-grey-4)] mb-2">Upcoming meetings</div>
            {meetingsState.loading && <SectionSkeleton testId="client-portal-meetings-loading" />}
            {meetingsState.error && <SectionError onRetry={reloadMeetings} label="meetings" testId="client-portal-meetings-error" />}
            {!meetingsState.loading && !meetingsState.error && <MeetingsList meetings={meetings} calendlyUrl={calendlyUrl} />}
          </div>
          <div>
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--cp-grey-4)] mb-2">Recent project updates</div>
            {updatesState.loading && <SectionSkeleton testId="client-portal-updates-loading" />}
            {updatesState.error && <SectionError onRetry={reloadUpdates} label="project updates" testId="client-portal-updates-error" />}
            {!updatesState.loading && !updatesState.error && <UpdatesFeed updates={updates} onReact={onReact} />}
          </div>
        </section>

        <section id="documents" className="px-4 md:px-16 py-12 cp-section-rule scroll-mt-28 pb-20" data-testid="client-portal-section-documents">
          <span className="cp-eyebrow block mb-6">Documents</span>
          {documentsState.loading && <SectionSkeleton testId="client-portal-documents-loading" />}
          {documentsState.error && <SectionError onRetry={reloadDocuments} label="documents" testId="client-portal-documents-error" />}
          {!documentsState.loading && !documentsState.error && <DocumentsList documents={documents} fileUrl={(docId) => clientPortalApi.documentFileUrl(id, docId)} />}
        </section>
      </div>

      {activeDeliverable && <DeliverablePlayer projectId={id} deliverable={activeDeliverable} onClose={() => setActiveDeliverable(null)} />}
    </div>
  );
}
